import { useEffect, useMemo, useState } from 'react';
import type { Alert } from '@vigilo/shared';
import { get } from '@vigilo/api-client';
import { useApp } from '@/shell/AppContext';
import { alertActorLabel, type AlertActorUser } from '../utils/alert-actor';

const ALERT_HISTORY_PAGE_SIZE = 5;
const OPERATION_ALERT_HISTORY_PAGE_SIZE = 100;

type ActivityTrailEntry = NonNullable<Alert['triggerContext']['activityTrail']>[number];

interface AlertHistoryResult {
  data: Alert[];
  retentionMonths?: number;
}

export interface AlertHistoryEntry {
  action: ActivityTrailEntry['action'];
  actorLabel: string;
  note: string;
  timestamp: string;
}

export interface AlertHistoryState {
  entries: AlertHistoryEntry[];
  loading: boolean;
  error: string | null;
  retentionMonths: number | null;
}

export interface OperationAlertHistoryState {
  alerts: Alert[];
  loading: boolean;
  error: string | null;
  retentionMonths: number | null;
}

const ACTOR_LABEL_BY_ACTION: Record<ActivityTrailEntry['action'], string> = {
  created: 'System',
  acknowledged: 'Crowd Analyst',
  escalated: 'Crowd Analyst',
  resolved: 'Command Supervisor',
  reopened: 'Command Supervisor',
  assigned: 'Command Supervisor',
  noted: 'Crowd Analyst',
};

function unwrapHistoryResult(body: unknown): AlertHistoryResult | null {
  if (!body || typeof body !== 'object') return null;
  const direct = body as Partial<AlertHistoryResult>;
  if (Array.isArray(direct.data)) return direct as AlertHistoryResult;

  const envelope = body as { data?: unknown };
  const nested = envelope.data as Partial<AlertHistoryResult> | undefined;
  return nested && Array.isArray(nested.data) ? nested as AlertHistoryResult : null;
}

function normalizeActivityTrail(alert: Alert | null, currentUser: AlertActorUser | null): AlertHistoryEntry[] {
  const trail = alert?.triggerContext?.activityTrail;
  if (!Array.isArray(trail)) return [];

  return trail
    .filter((entry): entry is ActivityTrailEntry =>
      Boolean(entry?.action && entry?.timestamp && entry?.note),
    )
    .map(entry => ({
      action: entry.action,
      actorLabel: entry.actorId ? alertActorLabel(entry.actorId, currentUser) : ACTOR_LABEL_BY_ACTION[entry.action],
      note: entry.note,
      timestamp: entry.timestamp,
    }))
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
}

export function useAlertHistory(
  alertId: string | null | undefined,
  operationId: string | null | undefined,
  enabled = true,
): AlertHistoryState {
  const [historyAlert, setHistoryAlert] = useState<Alert | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retentionMonths, setRetentionMonths] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setHistoryAlert(null);
    setError(null);
    setRetentionMonths(null);

    if (!enabled || !alertId || !operationId) {
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const params = new URLSearchParams();
    params.set('operationId', operationId);
    params.set('q', alertId);
    params.set('pageSize', String(ALERT_HISTORY_PAGE_SIZE));
    params.set('sortBy', 'triggeredAt');
    params.set('sortOrder', 'DESC');

    setLoading(true);
    get<unknown>(`/alerts/history?${params.toString()}`)
      .then(body => {
        if (cancelled) return;
        const result = unwrapHistoryResult(body);
        const match = result?.data.find(item =>
          item.id === alertId && item.operationId === operationId,
        ) ?? null;
        setHistoryAlert(match);
        setRetentionMonths(result?.retentionMonths ?? null);
      })
      .catch(() => {
        if (!cancelled) setError('Unable to load alert lifecycle history.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [alertId, enabled, operationId]);

  const { currentUser } = useApp();
  const entries = useMemo(() => normalizeActivityTrail(historyAlert, currentUser), [currentUser, historyAlert]);

  return { entries, loading, error, retentionMonths };
}

export function useOperationAlertHistory(
  operationId: string | null | undefined,
  from: string | null | undefined,
  to: string | null | undefined,
  enabled = true,
): OperationAlertHistoryState {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retentionMonths, setRetentionMonths] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setAlerts([]);
    setError(null);
    setRetentionMonths(null);

    if (!enabled || !operationId || !from || !to) {
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const fromMs = Date.parse(from);
    const toMs = Date.parse(to);
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || fromMs >= toMs) {
      setLoading(false);
      setError('Invalid selected Operation alert history window.');
      return () => {
        cancelled = true;
      };
    }

    const params = new URLSearchParams();
    params.set('operationId', operationId);
    params.set('from', from);
    params.set('to', to);
    params.set('pageSize', String(OPERATION_ALERT_HISTORY_PAGE_SIZE));
    params.set('sortBy', 'triggeredAt');
    params.set('sortOrder', 'ASC');

    setLoading(true);
    get<unknown>(`/alerts/history?${params.toString()}`)
      .then(body => {
        if (cancelled) return;
        const result = unwrapHistoryResult(body);
        const rows = result?.data ?? [];
        setAlerts(rows.filter(alert => {
          const triggeredMs = Date.parse(alert.triggeredAt);
          return alert.operationId === operationId
            && Number.isFinite(triggeredMs)
            && triggeredMs >= fromMs
            && triggeredMs <= toMs;
        }));
        setRetentionMonths(result?.retentionMonths ?? null);
      })
      .catch(() => {
        if (!cancelled) setError('Unable to load selected Operation alert history.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, from, operationId, to]);

  return { alerts, loading, error, retentionMonths };
}
